import { Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, RouterModule, RouterOutlet } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, filter, map, Subject } from 'rxjs';
import { DB_PHASES } from '../../api/app-types/phase.app';
import { User } from '../../api/app-types/user.app';
import { USER_SERVICE } from '../../api/services/user.service';
import { Tab } from '../../core/components/tabs/tab';
import { Tabs } from '../../core/components/tabs/tabs';
import { AccessService } from '../../core/services/access.service';
import { CurrentUserService } from '../../core/services/current-user.service';
import { RECORD_MANAGER } from '../../core/services/record.provide';
import { REPRESENTATION_MODE } from '../../core/services/representation-mode.service';
import { STATE } from '../../core/services/state.service';
import { isRestrictedClient } from '../../shared/helpers/client-type.func';
import { splitPathAndQueryParameters } from '../../shared/helpers/routing.func';
import { hasPermission } from '../../shared/permissions/permission.func';
import { RecordFile, RecordType } from '../../shared/record/types';
import { RouteInjection } from '../../shared/routing/route-injection.class';
import { SessionManagerService } from '../../shared/storage/session-manager.service';
import { EmptyDesk } from './empty-desk';
import { VALIDATION_SERVICE } from '../../api/services/validation.service';
import { NOTIFICATION_SERVICE } from '../../shared/notification/notification';
import { createErrorNotification } from '../../shared/notification/notification.factory';

/**
 * TODO: This component needs to be refactored. There is to much logic for building the sidemenu etc. in here.
 * This should be moved to dedicated services/components.
 */
@Component({
	selector: 'lvr-desk',
	imports: [RouterOutlet, Tab, Tabs, RouterModule, EmptyDesk, TranslocoModule],
	templateUrl: './desk.html',
	styleUrl: './desk.scss',
})
export class Desk extends RouteInjection implements OnInit, OnDestroy {
	private readonly session = inject(SessionManagerService);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly state = inject(STATE);
	private readonly userApi = inject(USER_SERVICE);
	private readonly currentUserService = inject(CurrentUserService);
	private readonly validationApi = inject(VALIDATION_SERVICE);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly access = inject(AccessService);
	private readonly transloco = inject(TranslocoService);
	// sadly we need a subject to handle the destroy lifecycle
	private readonly _destroy = new Subject<void>();

	private readonly url = toSignal(
		this.router.events.pipe(
			filter((e): e is NavigationEnd => e instanceof NavigationEnd),
			map(() => this.router.url),
		),
		{ initialValue: this.router.url },
	);

	private readonly clientId = computed(() => {
		const ar = this.activeRecord();
		if (ar?.type === RecordType.CLIENT && ar.entityId) {
			const n = Number(ar.entityId);
			return Number.isFinite(n) ? n : NaN;
		}
		return NaN;
	});
	private readonly client = this.state.currentClient;
	private representationMode = inject(REPRESENTATION_MODE);
	private readonly currentUser = signal<User | null>(null);
	public readonly canReadAssignments = hasPermission(this.session.permissions, [
		'Assignments.Read',
		'Assignments.Read.PhaseOneOnly',
	]);
	public readonly canReadClientEnvironments = hasPermission(this.session.permissions, 'ClientEnvironments.Read');
	public readonly canReadDisabilityReports = hasPermission(this.session.permissions, [
		'Documents.DisabilityReport.Read',
		'Clients.Read.Restricted',
	]);
	public readonly canReadCareers = hasPermission(this.session.permissions, 'Careers.Read');
	public readonly canReadDocuments = hasPermission(this.session.permissions, [
		'Documents.General.Read',
		'Documents.Contact.Read',
		'Documents.Assignment.Read',
		'Documents.DisabilityReport.Read',
		'Documents.DisabilityKind.Read',
	]);
	public readonly canEditAssignments = hasPermission(this.session.permissions, 'Assignments.Edit');
	public readonly canTransitionPhases = hasPermission(this.session.permissions, 'Phases.Transition');
	public readonly openRecords = this.recordManager.openRecords;
	public readonly activeRecord = this.recordManager.activeRecord;
	public readonly showClientSideTabs = computed(() => this.activeRecord()?.type === RecordType.CLIENT);
	public readonly showInstitutionSideTabs = computed(() => this.activeRecord()?.type === RecordType.INSTITUTION);
	public readonly showSponsorSideTabs = computed(() => this.activeRecord()?.type === RecordType.SPONSOR);
	public readonly showUserSideTabs = computed(() => this.activeRecord()?.type === RecordType.USER);
	public readonly showIsoSideTabs = computed(() => this.activeRecord()?.type === RecordType.INTEGRATION_OFFICE_SERVICE);
	public readonly canReceiveRepresentation = signal(false);
	public readonly isRestricted = computed(() => isRestrictedClient(this.client()));
	private readonly activeClientId = computed(() => {
		const routeInfo = splitPathAndQueryParameters(this.router.url || '');
		return routeInfo.segments[1] === 'client' ? Number(routeInfo.segments[2]) : NaN;
	});
	public readonly hasAdditionalAssignmentAccess = computed(() => {
		const user = this.currentUser();
		if (!user) return false;
		const assignments = this.state.currentAssignments();
		const activeClientId = this.activeClientId();
		if (Number.isFinite(activeClientId)) {
			return this.access.canAccessAdditionalAssignmentsForClient(assignments, user, activeClientId);
		}
		return this.access.canAccessAdditionalAssignments(assignments, user);
	});
	private readonly assignmentInTransitionForUser = computed(() => {
		const user = this.currentUser();
		if (!user) return undefined;

		const transitions = this.state
			.currentAssignments()
			.flatMap((assignment) => [assignment, ...(assignment.additionalAssignments ?? [])])
			.filter(
				(assignment) =>
					assignment.phaseChangeId != null &&
					assignment.phaseChangeId > 0 &&
					assignment.phaseChangeId !== assignment.phaseId,
			);

		return transitions.find((assignment) =>
			assignment.isAdditionalAssignment
				? this.access.isAdditionalOwner(assignment, user)
				: this.access.isMainOwner(assignment, user),
		);
	});
	public readonly showPhaseSwitcher = computed(
		() => this.canTransitionPhases() && !!this.assignmentInTransitionForUser(),
	);
	public readonly phaseSwitcherLabel = computed(() => {
		const assignment = this.assignmentInTransitionForUser();
		if (!assignment) return null;

		const to = assignment.phaseChangeId;

		if (to === DB_PHASES.P4) return 'client.menu.unregister'; // Abmelden

		if (assignment.phaseId === DB_PHASES.P1 && (to === DB_PHASES.P2 || to === DB_PHASES.P3)) {
			return 'client.menu.register'; // Anmelden
		}

		return 'client.menu.transfer'; // Ummelden
	});
	public readonly showUserAssignmentMoveTab = computed(() => {
		this.url();
		return this.isActive(this.userAssignmentTransitionRoute());
	});
	public readonly isRepresentativeMode = this.representationMode.isActive;
	public readonly overviewRoute = computed(() => {
		const record = this.activeRecord();
		if (record?.type === RecordType.CLIENT && record.entityId) {
			return `/desk/client/${record.entityId}`;
		}
		if (record?.type === RecordType.INSTITUTION && record.entityId) {
			return `/desk/institution/${record.entityId}`;
		}
		if (record?.type === RecordType.SPONSOR && record.entityId) {
			return `/desk/sponsor/${record.entityId}`;
		}
		if (record?.type === RecordType.USER && record.entityId) {
			return `/desk/user/${record.entityId}`;
		}
		if (record?.type === RecordType.INTEGRATION_OFFICE_SERVICE && record.entityId) {
			return `/desk/integration-service-office/${record.entityId}`;
		}

		const currentUrl = this.router.url || record?.route || '';
		const routeInfo = splitPathAndQueryParameters(currentUrl);
		if (routeInfo.segments[1] === 'client' && routeInfo.segments[2] !== 'add') {
			return `/desk/client/${routeInfo.segments[2]}`;
		}
		if (routeInfo.segments[1] === 'institution' && routeInfo.segments[2] !== 'add') {
			return `/desk/institution/${routeInfo.segments[2]}`;
		}
		if (routeInfo.segments[1] === 'sponsor') {
			return `/desk/sponsor/${routeInfo.segments[2]}`;
		}
		if (routeInfo.segments[1] === 'user' && routeInfo.segments[2] !== 'add') {
			return `/desk/user/${routeInfo.segments[2]}`;
		}
		if (routeInfo.segments[1] === 'integration-service-office' && routeInfo.segments[2] !== 'add') {
			return `/desk/integration-service-office/${routeInfo.segments[2]}`;
		}
		return '';
	});

	public readonly contactPersonRoute = computed(() => `${this.overviewRoute()}/persons`);

	public readonly assignmentRoute = computed(() => `${this.overviewRoute()}/assignments`);
	public readonly userAssignmentTransitionRoute = computed(() => `${this.overviewRoute()}/assignments/move`);
	public readonly userRepresentationRoute = computed(() => `${this.overviewRoute()}/representation`);
	public readonly assignmentTransitionRoute = computed(() => {
		const assignment = this.assignmentInTransitionForUser();
		if (!assignment) return '';
		const suffix = assignment?.isServiceOfficeStatement
			? 'fds-move'
			: assignment?.isAdditionalAssignment
				? 'additional-move'
				: 'move';
		return `${this.overviewRoute()}/assignments/${assignment?.id}/${suffix}`;
	});
	public readonly disabilitiesRoute = computed(() => `${this.overviewRoute()}/disabilities`);
	public readonly relativesRoute = computed(() => `${this.overviewRoute()}/relatives`);
	public readonly careerRoute = computed(() => `${this.overviewRoute()}/career`);
	public readonly workplaceRoute = computed(() => `${this.overviewRoute()}/workplace`);
	public readonly documentsRoute = computed(() => `${this.overviewRoute()}/documents`);

	public readonly moveSuffixes = ['move', 'fds-move', 'additional-move'];

	// Scroll handling for tab content container. The scroll is NOT in the window but in 'tab-content'
	public readonly tabContent = viewChild('tabContent', {
		read: ElementRef<HTMLElement>,
	});

	private readonly navigationEndUrl = toSignal(
		this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd),
			map((event) => event.urlAfterRedirects),
		),
		{ initialValue: this.router.url },
	);

	public constructor() {
		super();
		void this.loadCurrentUser();
		effect(() => this.checkForActiveRecord());

		effect(() => {
			const id = this.clientId();
			if (!Number.isFinite(id)) {
				return;
			}
			this.state.setCurrentClient(id);
		});

		// Reset scroll position on navigation
		effect(() => {
			// Trigger on navigation
			this.navigationEndUrl();

			const containerRef = this.tabContent();
			if (!containerRef) return;

			// Use microtask to ensure child view is rendered before scrolling
			queueMicrotask(() => {
				containerRef.nativeElement.scrollTop = 0;
			});
		});

		// Can receive representation
		effect(() => {
			void this.validateCanBeRepresented(this.activeRecord());
		});
	}

	private async loadCurrentUser() {
		try {
			const user = await this.currentUserService.getCurrentUser();
			this.currentUser.set(user);
		} catch {
			this.currentUser.set(null);
		}
	}

	public ngOnInit() {
		const url = this.router.url;
		this.handleNavigation(url);
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map((event: NavigationEnd) => event.url),
				filter((url) => url.startsWith('/desk')),
				distinctUntilChanged(),
			)
			.subscribe((url: string) => {
				this.handleNavigation(url);
			});
	}

	public ngOnDestroy() {
		this._destroy.next();
		this._destroy.complete();
	}

	public isActive = (route?: string, options: { exact?: boolean; exclude?: string[] } = {}) => {
		if (!route) return false;

		const { exact = false, exclude = [] } = options;
		const target = splitPathAndQueryParameters(route);
		const current = splitPathAndQueryParameters(this.router.url);

		if (exclude?.some((segment) => current.path.includes(`/${segment}`))) {
			return false;
		}

		if (this.isMovePath(target.path)) {
			return this.isExactMovePathMatch(current.path, target.path);
		}

		if (exact) {
			return current.path === target.path || current.path === `${target.path}/edit`;
		}

		return current.path === target.path || current.path.startsWith(`${target.path}/`);
	};

	private isMovePath(path: string): boolean {
		return this.moveSuffixes.some((suffix) => path.endsWith(suffix));
	}

	private getMoveSuffix(path: string): string | undefined {
		return this.moveSuffixes.find((suffix) => path.endsWith(suffix));
	}

	private removeMoveSuffix(path: string): string {
		const suffix = this.getMoveSuffix(path);
		return suffix ? path.slice(0, -suffix.length) : path;
	}

	private isExactMovePathMatch(currentPath: string, targetPath: string): boolean {
		const currentSuffix = this.getMoveSuffix(currentPath);
		const targetSuffix = this.getMoveSuffix(targetPath);

		if (!currentSuffix || !targetSuffix) return false;

		const sameBasePath = this.removeMoveSuffix(currentPath) === this.removeMoveSuffix(targetPath);
		const sameMoveType = currentSuffix === targetSuffix;

		return sameBasePath && sameMoveType;
	}

	public closeRecord(recordId: string) {
		if (!recordId) return;
		this.recordManager.closeRecord(recordId);
	}

	private handleNavigation(url: string) {
		const routeInfo = splitPathAndQueryParameters(url);
		if (routeInfo.path === '/desk/client/add') {
			this.recordManager.openRecord(RecordType.CREATE_CLIENT, {
				route: url,
			});
			return;
		}

		// Check if route contains 'desk/client/' and has a valid ID
		if (routeInfo.path.indexOf('/desk/client/') >= 0 && routeInfo.path.split('/').length > 3) {
			const clientId = routeInfo.path.split('/')[3];
			if (clientId) {
				this.recordManager.openRecord(RecordType.CLIENT, {
					entityId: clientId,
					route: url,
					title: routeInfo.queryParams['displayName'] ?? undefined,
				});
			}
			return;
		}

		if (routeInfo.path === '/desk/user/add') {
			this.recordManager.openRecord(RecordType.CREATE_USER, {
				route: url,
			});
			return;
		}

		if (routeInfo.path === '/desk/institution/add') {
			this.recordManager.openRecord(RecordType.CREATE_INSTITUTION, {
				route: url,
			});
			return;
		}

		// Check if route contains 'desk/institution/' and has a valid ID
		if (routeInfo.path.indexOf('/desk/institution/') >= 0 && routeInfo.path.split('/').length > 3) {
			const institutionId = routeInfo.path.split('/')[3];
			if (institutionId) {
				this.recordManager.openRecord(RecordType.INSTITUTION, {
					entityId: institutionId,
					route: url,
				});
			}
			return;
		}

		// Check if route contains 'desk/sponsor/' and has a valid ID
		if (routeInfo.path.indexOf('/desk/sponsor/') >= 0 && routeInfo.path.split('/').length > 3) {
			const sponsorId = routeInfo.path.split('/')[3];
			if (sponsorId) {
				this.recordManager.openRecord(RecordType.SPONSOR, {
					entityId: sponsorId,
					route: url,
				});
			}
			return;
		}

		if (routeInfo.path === '/desk/network-contact/add') {
			this.recordManager.openRecord(RecordType.CREATE_NETWORK_CONTACT, {
				route: url,
			});
			return;
		}

		// Check if route contains 'desk/network-contact/' and has a valid ID
		if (routeInfo.path.indexOf('/desk/network-contact/') >= 0 && routeInfo.path.split('/').length > 3) {
			const contactId = routeInfo.path.split('/')[3];
			if (contactId) {
				this.recordManager.openRecord(RecordType.NETWORK_CONTACT, {
					entityId: 'network-contact',
					route: url,
				});
			}
			return;
		}
		// Check if route contains 'desk/user/' and has a valid ID
		if (routeInfo.path.indexOf('/desk/user/') >= 0 && routeInfo.path.split('/').length > 3) {
			const userId = routeInfo.path.split('/')[3];
			if (userId) {
				this.recordManager.openRecord(RecordType.USER, {
					entityId: userId,
					route: url,
				});
			}
			return;
		}

		// Check if route contains 'desk/integration-service-office/' and has a valid ID
		if (routeInfo.path.indexOf('/desk/integration-service-office/') >= 0 && routeInfo.path.split('/').length > 3) {
			const userId = routeInfo.path.split('/')[3];
			if (userId) {
				this.recordManager.openRecord(RecordType.INTEGRATION_OFFICE_SERVICE, {
					entityId: userId,
					route: url,
				});
			}
			return;
		}

		this.checkForActiveRecord();
	}

	private checkForActiveRecord() {
		const activeRecord = this.recordManager.activeRecord();
		if (!activeRecord) return;
		const routeInfo = splitPathAndQueryParameters(activeRecord.route);
		if (!routeInfo) return;
		this.router.navigate([routeInfo.path], {
			queryParams: routeInfo.queryParams,
		});
	}

	private async validateCanBeRepresented(activeRecord: RecordFile | undefined) {
		this.canReceiveRepresentation.set(false);

		if (!activeRecord || activeRecord.type !== RecordType.USER) return;

		try {
			const specialist = await this.userApi.getSingle(activeRecord.entityId as string);
			const validation = await this.validationApi.validateCanReceiveRepresentation(specialist.id);

			this.canReceiveRepresentation.set(validation);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(
					this.transloco.translate('base.notification_message.user_representation_validation_failed'),
				),
			);
			this.canReceiveRepresentation.set(false);
		}
	}
}
